export function toInt(value, fallback = 0) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
}

export function toFloat(value, fallback = 0) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
}

export function buildProductPayload(value, productType, includeCode = false) {
    // ⚠️ El tipo se manda TAL CUAL, no colapsado.
    //
    // Antes esto decía `Number(productType) === 2 ? 2 : 1`, o sea que cualquier tipo distinto de 2
    // se mandaba como 1. Un juego USADO (pt=3) guardado desde esta ficha se convertía en juego
    // nuevo, y del otro lado `syncSpecialization` BORRA la fila `used_game` — con el vendedor, la
    // condición y el `agreed_credit`, que es lo que el local le debe al vendedor en puntos.
    //
    // Verificado contra la base: `used_game existe: true, agreed_credit: 500` antes del PATCH,
    // `used_game existe: false, productType: 1` después. En prod quedó un caso real
    // (`Némesis: lockdown`, code 1788562355530).
    //
    // Los usados ya no llegan acá —EditProductPage los manda a /editUsedGame— y el backend además
    // los rechaza, pero este payload no tiene por qué mentir sobre el tipo de todos modos.
    const tipo = Number(productType) || 1;
    const payload = {
        productType: tipo,
        prefix: value.prefix,
        name: value.name,
        price: toFloat(value.price),
        discount: toInt(value.discount),
        stock: toInt(value.stock),
        minStock: toInt(value.minStock),
        suggestionStock: toInt(value.suggestionStock),
        description: value.description,
        type: toInt(value.type),
        provider: toInt(value.provider),
        relatedProducts: value.relatedProducts || [],
        externalItems: value.externalItems || [],
        sku: value.sku,
        display: value.display === 'true',
        barCode: toInt(value.barCode),
        codeML: value.codeML || '',
        cost: toInt(value.cost),
    };

    // `isNew` viaja SÓLO si el form lo puso en `value` (dirty-tracking: ver FormBoxGameComponent
    // / FormAccessoryComponent). No es un detalle de estilo, es el corazón del fix:
    //
    // Del otro lado, `isNew` ya no es una columna sino la membresía del producto en la colección
    // "Novedades", y el backend interpreta la AUSENCIA de la key como "no toques la membresía".
    // Si mandáramos el campo siempre —como hacía este payload, leyendo el DOM cargado al abrir
    // la ficha—, corregir una falta de ortografía a las 10:10 reescribiría la curación con lo
    // que el form leyó a las 10:00: si el dueño sacó ese juego de Novedades a las 10:05 desde el
    // ABM de colecciones, VUELVE SOLO. Un form que emite el objeto completo pisa lo que no tocó.
    if (value.isNew !== undefined) {
        payload.isNew = value.isNew === 'true';
    }

    if (includeCode) {
        payload.code = toInt(value.code);
    }

    if (tipo === 1) {
        return {
            ...payload,
            category: toInt(value.category),
            gameDuration: toInt(value.gameDuration),
            minPlayers: toInt(value.minPlayers),
            maxPlayers: toInt(value.maxPlayers),
            difficulty: toInt(value.difficulty),
            cooperative: value.cooperative === 'true',
            lonely: value.lonely === 'true',
            forTwo: value.forTwo === 'true',
            expansion: value.expansion === 'true',
            onlyUse: value.onlyUse === 'true',
            popularity: toInt(value.popularidad, 3),
        };
    }

    return payload;
}

// Carpeta del bucket donde viven las imágenes del producto. TIENE que coincidir con la que
// arma el storefront para leerlas (frontend: config/firebase.ts, generateProductThumbnail); si
// divergen, la imagen se sube "bien" y el cliente ve el placeholder — que es exactamente lo que
// pasaba con los relacionados en ENI-228.
// Ojo con el tipo 3 (usado): acá caía en /products y el storefront lo buscaba en /accessories.
// No se notaba porque no existía forma de subirle imágenes a un usado; al habilitarla, esa
// discrepancia se volvía un bug. Ahora tiene carpeta propia, alineada en los dos lados.
export function getProductImageFolder(productType, code) {
    const type = Number(productType);
    const folder = type === 2 ? '/accessories' : (type === 3 ? '/used' : '/products');
    return `${folder}/${code}`;
}

export function getProductsListPath(productType) {
    const normalizedProductType = Number(productType);

    if (normalizedProductType === 1 || normalizedProductType === 2) {
        return `/products?productType=${normalizedProductType}`;
    }

    return '/products';
}

// ─────────────────────────────────────────────────────────────────────────────
// Vínculos con publicaciones de MercadoLibre
//
// Un id de publicación de ML en Uruguay es 'MLU' + dígitos. El botón "+" de estos formularios
// agregaba el renglón nuevo con la cadena literal "New" como valor, así que guardar sin escribir
// nada persistía `externalItemId = 'New'` en `external_item`. En producción quedaron DOS filas
// así, y cada movimiento de stock de esos productos hace `PUT /items/New` -> 400
// `item.id.invalid`, para siempre, porque el error sólo va a un log que nadie mira.
//
// El renglón nuevo ahora arranca vacío (se ve el placeholder) y el guardado avisa antes de
// mandar. El backend además descarta lo que no tenga forma de publicación, pero el aviso útil
// es éste: es donde está la persona que lo tipeó.
// ─────────────────────────────────────────────────────────────────────────────
export const FORMATO_MLU = /^MLU\d+$/;

/** Saca espacios, normaliza la variante con guion y descarta los renglones vacíos. */
export function limpiarExternalIds(externalIds = []) {
    return (Array.isArray(externalIds) ? externalIds : [])
        .map((item) => String(item || '').trim().replace('MLU-', 'MLU'))
        .filter(Boolean);
}

/** Los que sobreviven al limpiado pero no son una publicación. Vacío = se puede guardar. */
export function externalIdsInvalidos(externalIds = []) {
    return limpiarExternalIds(externalIds).filter((item) => !FORMATO_MLU.test(item));
}
